import { router, useForm } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { Key, AlertCircle } from 'lucide-react'
import { FormEvent } from 'react'

const SERVICES = [
  { value: 'openai', label: 'OpenAI', icon: '🤖', placeholder: 'sk-proj-...' },
  { value: 'anthropic', label: 'Anthropic', icon: '🧠', placeholder: 'sk-ant-...' },
  { value: 'google_gemini', label: 'Google Gemini', icon: '✨', placeholder: 'AIza...' },
  { value: 'sendgrid', label: 'SendGrid', icon: '📧', placeholder: 'SG...' },
]

function New() {
  const { t } = useTranslation('admin')
  const { data, setData, post, processing, errors } = useForm({
    service_name: '',
    secret_name: '',
    value: '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/admin/cayu_provided_secrets')
  }

  const selectedService = SERVICES.find(s => s.value === data.service_name)

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Cayu Provided Keys', href: '/admin/cayu_provided_secrets' },
          { label: 'New' },
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6 max-w-2xl">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground">{t('cayuProvidedKeys.new.title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('cayuProvidedKeys.new.description')}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6 space-y-6">
                  {/* Service Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="service_name">
                      {t('cayuProvidedKeys.form.service')} <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={data.service_name}
                      onValueChange={(value) => setData('service_name', value)}
                    >
                      <SelectTrigger id="service_name">
                        <SelectValue placeholder={t('cayuProvidedKeys.form.selectService')} />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((service) => (
                          <SelectItem key={service.value} value={service.value}>
                            <div className="flex items-center gap-2">
                              <span>{service.icon}</span>
                              <span>{service.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.service_name && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.service_name}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {t('cayuProvidedKeys.form.serviceHint')}
                    </p>
                  </div>

                  {/* Key Name */}
                  <div className="space-y-2">
                    <Label htmlFor="secret_name">
                      {t('cayuProvidedKeys.form.keyName')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="secret_name"
                      type="text"
                      placeholder={t('cayuProvidedKeys.form.keyNamePlaceholder')}
                      value={data.secret_name}
                      onChange={(e) => setData('secret_name', e.target.value)}
                      required
                    />
                    {errors.secret_name && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.secret_name}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {t('cayuProvidedKeys.form.keyNameHint')}
                    </p>
                  </div>

                  {/* API Key Value */}
                  <div className="space-y-2">
                    <Label htmlFor="value">
                      {t('cayuProvidedKeys.form.apiKeyValue')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="value"
                      type="password"
                      placeholder={selectedService?.placeholder || t('cayuProvidedKeys.form.enterApiKey')}
                      value={data.value}
                      onChange={(e) => setData('value', e.target.value)}
                      required
                      className="font-mono text-sm"
                    />
                    {errors.value && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.value}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {t('cayuProvidedKeys.form.apiKeyHint')}
                    </p>
                  </div>

                  {/* Security Notice */}
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Key className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-purple-900">{t('cayuProvidedKeys.security.title')}</h3>
                        <p className="text-sm text-purple-700 mt-1">
                          {t('cayuProvidedKeys.security.newKeyNote')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => router.visit('/admin/cayu_provided_secrets')}
                    disabled={processing}
                  >
                    {t('cayuProvidedKeys.actions.cancel')}
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? t('cayuProvidedKeys.actions.creating') : t('cayuProvidedKeys.actions.createKey')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
